import { NotificationItem } from '@/components/notifications/NotificationItem';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useNotificationActions, useNotifications, useNotificationSummary } from '@/hooks/queries/useNotifications';
import { useAuth } from '@/hooks/useAuth';
import { getNotificationLink, isNotificationUnread } from '@/lib/notifications';
import type { AppNotificationDto } from '@/types/dto/notification.dto';
import { Bell, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export function NotificationBell() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [open, setOpen] = useState(false);
  const { notifications, isError, isLoading, refetch } = useNotifications(user?.id, 5, isAuthenticated);
  const { unreadCount } = useNotificationSummary(user?.id, isAuthenticated);
  const { markRead } = useNotificationActions(user?.id);

  if (!isAuthenticated || !user) {
    return null;
  }

  const openNotification = async (notification: AppNotificationDto) => {
    if (isNotificationUnread(notification)) {
      await markRead(notification.id).catch(() => undefined);
    }

    const link = getNotificationLink(notification);

    if (link) {
      setOpen(false);
      navigate(link);
    }
  };

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="hover:bg-muted relative flex h-10 w-10 items-center justify-center rounded-xl transition-colors"
          aria-label={unreadCount ? `Notificações, ${unreadCount} não lidas` : 'Notificações'}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 ? (
            <span className="bg-primary text-primary-foreground absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold ring-2 ring-white">
              {badgeLabel}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[min(24rem,calc(100vw-1rem))] overflow-hidden p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold">Notificações</h2>
            <p className="text-muted-foreground text-xs">
              {unreadCount ? `${unreadCount} não ${unreadCount === 1 ? 'lida' : 'lidas'}` : 'Tudo em dia'}
            </p>
          </div>
          <Link
            to="/notificacoes"
            onClick={() => setOpen(false)}
            className="text-primary text-xs font-semibold hover:underline"
          >
            Ver todas
          </Link>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground flex h-36 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin" aria-label="Carregando notificações" />
          </div>
        ) : isError ? (
          <div className="px-6 py-8 text-center">
            <p className="text-muted-foreground text-sm">Não foi possível carregar suas notificações.</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="text-primary mt-2 text-sm font-semibold hover:underline"
            >
              Tentar novamente
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <Bell className="text-muted-foreground/50 mx-auto h-7 w-7" aria-hidden="true" />
            <p className="text-foreground mt-3 text-sm font-medium">Nenhuma notificação por aqui</p>
            <p className="text-muted-foreground mt-1 text-xs">Avisaremos quando houver novidades.</p>
          </div>
        ) : (
          <div className="max-h-[24rem] divide-y overflow-y-auto">
            {notifications.map((notification) => (
              <NotificationItem key={notification.id} notification={notification} compact onOpen={openNotification} />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
